<div class="config-form-container">
	<form [formGroup]="configForm" *ngIf="!loading">
		<div class="row mb-3">
			<div class="col-md-12">
				<mat-slide-toggle formControlName="enabled">Enable 3PL Integration</mat-slide-toggle>
			</div>
		</div>

		<div class="row mb-3">
			<div class="col-md-6">
				<mat-form-field appearance="fill" class="w-100">
					<mat-label>Integration Type</mat-label>
					<mat-select formControlName="integration_type">
						<mat-option value="API">API</mat-option>
						<mat-option value="PGS">PGS</mat-option>
						<mat-option value="EDI">EDI</mat-option>
						<mat-option value="SFTP">SFTP</mat-option>
						<mat-option value="FTP">FTP</mat-option>
					</mat-select>
				</mat-form-field>
			</div>
			<div class="col-md-6">
				<mat-form-field appearance="fill" class="w-100">
					<mat-label>3PL Warehouse Code</mat-label>
					<input matInput formControlName="warehouse_code" required placeholder="e.g. CM, PICO, FW, NJ, 00">
					<mat-hint>3PL facility identifier (not an ERP location). Reorder reports use ERP locations only.</mat-hint>
				</mat-form-field>
			</div>
		</div>

		<div class="row mb-3">
			<div class="col-md-12">
				<mat-slide-toggle formControlName="test_mode">Test Mode</mat-slide-toggle>
			</div>
		</div>

		<!-- Order export -->
		<div formGroupName="config_json" class="config-section mb-3">
			<h5>Order export</h5>
			<p class="text-muted small mb-2">
				Order export rules by <strong>sales order status</strong> (same list as elsewhere in sales). Leave all selections empty on a row to use server defaults for that rule.
			</p>
			<p *ngIf="orderStatusesForSelect.length === 0" class="text-warning small mb-2">Status list not loaded yet; refresh the page if dropdowns stay empty.</p>
			<div class="row">
				<div class="col-md-4">
					<mat-form-field appearance="fill" class="w-100">
						<mat-label>Allowed statuses (export)</mat-label>
						<mat-select formControlName="allowed_statuses" multiple placeholder="Default — any allowed by server">
							<mat-option *ngFor="let s of orderStatusesForSelect" [value]="+s.order_status_id">{{ s.name }} ({{ s.order_status_id }})</mat-option>
						</mat-select>
						<mat-hint>Only these statuses may be exported (when set)</mat-hint>
					</mat-form-field>
				</div>
				<div class="col-md-4">
					<mat-form-field appearance="fill" class="w-100">
						<mat-label>Blocked statuses</mat-label>
						<mat-select formControlName="blocked_statuses" multiple placeholder="None blocked">
							<mat-option *ngFor="let s of orderStatusesForSelect" [value]="+s.order_status_id">{{ s.name }} ({{ s.order_status_id }})</mat-option>
						</mat-select>
						<mat-hint>Never export orders in these statuses</mat-hint>
					</mat-form-field>
				</div>
				<div class="col-md-4">
					<mat-form-field appearance="fill" class="w-100">
						<mat-label>Shipped statuses</mat-label>
						<mat-select formControlName="shipped_statuses" multiple placeholder="Default — server shipped list">
							<mat-option *ngFor="let s of orderStatusesForSelect" [value]="+s.order_status_id">{{ s.name }} ({{ s.order_status_id }})</mat-option>
						</mat-select>
						<mat-hint>Treated as already shipped / skip re-export</mat-hint>
					</mat-form-field>
				</div>
			</div>
			<div class="row mt-3">
				<div class="col-md-12">
					<h6 class="mb-2">Stock checks before sending to 3PL</h6>
					<p class="text-muted small mb-3">
						Before the auto worker sends an order, it checks how much stock is on hand at the fillability warehouse. There are two situations to handle:
					</p>

					<div class="mb-3 pl-3" style="border-left: 3px solid #ddd;">
						<strong class="d-block mb-1">A. Some items in stock, some not (partial)</strong>
						<p class="text-muted small mb-2">
							Move the out-of-stock lines onto a new back-order sales order (zero freight) so the in-stock lines can ship now. The original order keeps only what can ship.
						</p>
						<mat-slide-toggle formControlName="split_partial_backorder_enabled">Allow splitting partial orders (manual via queue / API)</mat-slide-toggle>
						<mat-slide-toggle formControlName="auto_split_partial_before_export" class="d-block mt-2">Automatically split partial orders before sending to 3PL</mat-slide-toggle>
						<mat-form-field appearance="fill" class="w-100 mt-2" style="max-width: 440px">
							<mat-label>Status to assign the new back-order SO</mat-label>
							<mat-select formControlName="backorder_orderstatusid">
								<mat-option [value]="">— Look up by name &ldquo;BackOrder&rdquo;</mat-option>
								<mat-option *ngFor="let s of orderStatusesForSelect" [value]="+s.order_status_id">{{ s.name }} ({{ s.order_status_id }})</mat-option>
							</mat-select>
						</mat-form-field>
						<mat-slide-toggle formControlName="queue_backorder_for_tpl3pl" class="d-block mt-2">Also queue the new back-order SO for 3PL (uncommon)</mat-slide-toggle>
					</div>

					<div class="pl-3" style="border-left: 3px solid #ddd;">
						<strong class="d-block mb-1">B. Nothing in stock (fully unfillable)</strong>
						<p class="text-muted small mb-2">
							Don&apos;t send the order to the 3PL at all &mdash; hold it in the queue and re-check later. Once stock arrives, the worker will pick it back up and send it automatically.
						</p>
						<mat-slide-toggle formControlName="skip_not_fillable_orders">Hold fully unfillable orders instead of sending them</mat-slide-toggle>
						<mat-form-field appearance="fill" class="w-100 mt-2" style="max-width: 280px">
							<mat-label>Re-check after (minutes)</mat-label>
							<input matInput type="number" min="1" formControlName="not_fillable_hold_minutes">
							<mat-hint>How long to wait before checking stock again. Default 60.</mat-hint>
						</mat-form-field>
					</div>
				</div>
			</div>

			<div class="row mt-4">
				<div class="col-md-12">
					<h6 class="mb-2">Payment checks on split back-orders</h6>
					<p class="text-muted small mb-3">
						When a partial order is split, the new back-order keeps the customer&apos;s payment method (if one was attached). For customers on must-pay terms (PPD, credit card only, etc.) <em>without</em> a payment method on the source order, the back-order is bumped to <strong>Hold For Payment</strong> instead of being auto-sent to the 3PL. Net-terms customers (Net 15, Net 30, ...) are not affected.
					</p>

					<div class="pl-3" style="border-left: 3px solid #ddd;">
						<mat-form-field appearance="fill" class="w-100" style="max-width: 440px">
							<mat-label>&ldquo;Hold For Payment&rdquo; status to assign</mat-label>
							<mat-select formControlName="hold_for_payment_orderstatusid" [compareWith]="compareByStatusId">
								<mat-option [value]="">— Use global <code>HoldForPaymentOrderStatusId</code> / name lookup</mat-option>
								<mat-option *ngFor="let s of orderStatusesForSelect" [value]="+s.order_status_id">{{ s.name }} ({{ s.order_status_id }})</mat-option>
							</mat-select>
							<mat-hint>Status used when bumping a split back-order off the 3PL queue.</mat-hint>
						</mat-form-field>

						<mat-form-field appearance="fill" class="w-100 mt-2" style="max-width: 640px">
							<mat-label>Always treat as MUST-PAY (force Hold For Payment)</mat-label>
							<mat-select formControlName="must_pay_payterms" multiple [compareWith]="comparePayterms">
								<mat-option *ngFor="let t of paymentTermsForSelect" [value]="t.termsindicator">
									{{ t.terms }} <span class="text-muted">({{ t.termsindicator }})</span>
								</mat-option>
							</mat-select>
							<mat-hint>Overrides the auto-detect (paymentterms.cc_process). Use for PPD, COD, credit-card-only codes, etc.</mat-hint>
						</mat-form-field>

						<mat-form-field appearance="fill" class="w-100 mt-2" style="max-width: 640px">
							<mat-label>Always treat as NET / on-account (allow split without payment)</mat-label>
							<mat-select formControlName="net_payterms" multiple [compareWith]="comparePayterms">
								<mat-option *ngFor="let t of paymentTermsForSelect" [value]="t.termsindicator">
									{{ t.terms }} <span class="text-muted">({{ t.termsindicator }})</span>
								</mat-option>
							</mat-select>
							<mat-hint>Overrides the auto-detect. Use for terms that don&apos;t match the &ldquo;NET N&rdquo; pattern but should still ship on credit.</mat-hint>
						</mat-form-field>
					</div>
				</div>
			</div>
		</div>

		<!-- Shipment & invoicing -->
		<div formGroupName="config_json" class="config-section mb-3">
			<h5>Shipment &amp; invoicing</h5>
			<p class="text-muted small mb-2">
				<strong>Shipment import</strong> saves the shipment (and tracking when present). It can set <strong>order status</strong> from the shipment/tracking dropdowns below. It does not change line <code>qtyinvoiced</code> or post inventory/GL by itself.
			</p>
			<p class="text-muted small mb-2">
				<strong>Auto-invoice</strong> runs in the same run when enabled: the normal sales invoice pipeline (same as invoicing in the UI)—AR, stock moves, GL, then <code>qtyinvoiced</code> / completed lines. Turn it off if you invoice manually after ship.
			</p>
			<div *ngIf="integrationType === 'PGS'" class="alert alert-secondary border mb-3 py-2 px-3">
				<p class="small mb-1"><strong>PGS Status Sync</strong> (section above) updates status when the PGS status poll runs, using your keyword rules and rank order.</p>
				<p class="small mb-0"><strong>This section</strong> updates status when an order is exported, when a 3PL shipment is processed, when you approve a shipment in the UI, or when tracking on the order is updated. Pick statuses so those moments follow the workflow you want; they are separate code paths from PGS keyword sync.</p>
			</div>
			<div class="row mb-2">
				<div class="col-md-12">
					<mat-slide-toggle formControlName="auto_invoice_on_shipment">Auto-invoice on shipment</mat-slide-toggle>
					<mat-hint class="d-block" *ngIf="configForm.get('config_json')?.get('auto_invoice_on_shipment')?.value">Shipments will be automatically invoiced right after import.</mat-hint>
					<mat-hint class="d-block" *ngIf="!configForm.get('config_json')?.get('auto_invoice_on_shipment')?.value">Shipments will require manual approval before invoicing.</mat-hint>
				</div>
			</div>
			<div class="row mb-2 mt-2">
				<div class="col-md-12">
					<mat-slide-toggle formControlName="email_customer_on_3pl_invoice">Email customer on invoice</mat-slide-toggle>
					<mat-hint class="d-block" *ngIf="configForm.get('config_json')?.get('email_customer_on_3pl_invoice')?.value">After a 3PL shipment is invoiced, the customer will receive an email with tracking details and the invoice PDF attached.</mat-hint>
					<mat-hint class="d-block" *ngIf="!configForm.get('config_json')?.get('email_customer_on_3pl_invoice')?.value">No customer email will be sent when a 3PL shipment is invoiced.</mat-hint>
				</div>
			</div>

			<h6 class="text-muted mt-3 mb-2">Order status — after export</h6>
			<p class="text-muted small mb-2">Runs once when the 3PL accepts the order (successful export). Only applies if the order's current status is below the status you choose, except pre-auth statuses (52/53) which are promoted.</p>
			<div class="row mb-3">
				<div class="col-md-6 col-lg-4">
					<mat-form-field appearance="fill" class="w-100">
						<mat-label>Status when exported to 3PL</mat-label>
						<mat-select formControlName="status_on_export_success">
							<mat-option [value]="">— Not set (server default: 5 Picking)</mat-option>
							<mat-option *ngFor="let s of orderStatusesForSelect" [value]="+s.order_status_id">{{ s.name }} ({{ s.order_status_id }})</mat-option>
						</mat-select>
						<mat-hint>Typical: Picking (5). Only updates if current status is below the chosen status (or 52/53).</mat-hint>
					</mat-form-field>
				</div>
			</div>

			<h6 class="text-muted mt-2 mb-2">Order status — shipment &amp; tracking</h6>
			<p class="text-muted small mb-2">These run when shipment data is imported or when tracking on the order is refreshed after import. "In transit" and "delivered" use saved tracking rows on the order; they may do nothing until the carrier/tracking feed populates tracking status or dates.</p>
			<div class="row">
				<div class="col-md-6 col-lg-4">
					<mat-form-field appearance="fill" class="w-100">
						<mat-label>Status when shipment received</mat-label>
						<mat-select formControlName="status_on_shipment_received">
							<mat-option [value]="">— Use shipped statuses</mat-option>
							<mat-option *ngFor="let s of orderStatusesForSelect" [value]="+s.order_status_id">{{ s.name }} ({{ s.order_status_id }})</mat-option>
						</mat-select>
						<mat-hint>When a 3PL shipment record is processed (import path).</mat-hint>
					</mat-form-field>
				</div>
				<div class="col-md-6 col-lg-4">
					<mat-form-field appearance="fill" class="w-100">
						<mat-label>Status when in transit</mat-label>
						<mat-select formControlName="status_on_tracking_in_transit">
							<mat-option [value]="">— Skip</mat-option>
							<mat-option *ngFor="let s of orderStatusesForSelect" [value]="+s.order_status_id">{{ s.name }} ({{ s.order_status_id }})</mat-option>
						</mat-select>
						<mat-hint>When tracking shows a carrier status on the order.</mat-hint>
					</mat-form-field>
				</div>
				<div class="col-md-6 col-lg-4">
					<mat-form-field appearance="fill" class="w-100">
						<mat-label>Status when delivered</mat-label>
						<mat-select formControlName="status_on_tracking_delivered">
							<mat-option [value]="">— Skip</mat-option>
							<mat-option *ngFor="let s of orderStatusesForSelect" [value]="+s.order_status_id">{{ s.name }} ({{ s.order_status_id }})</mat-option>
						</mat-select>
						<mat-hint>When estimated delivery date on tracking is in the past.</mat-hint>
					</mat-form-field>
				</div>
				<div class="col-md-6 col-lg-4">
					<mat-form-field appearance="fill" class="w-100">
						<mat-label>Status when invoice failed</mat-label>
						<mat-select formControlName="status_on_invoice_failed">
							<mat-option [value]="">— Skip</mat-option>
							<mat-option *ngFor="let s of orderStatusesForSelect" [value]="+s.order_status_id">{{ s.name }} ({{ s.order_status_id }})</mat-option>
						</mat-select>
						<mat-hint>After auto-invoice fails (e.g. 91 Needs Invoiced).</mat-hint>
					</mat-form-field>
				</div>
				<div class="col-md-6 col-lg-4">
					<mat-form-field appearance="fill" class="w-100">
						<mat-label>Status when shipment approved</mat-label>
						<mat-select formControlName="status_on_shipment_approved">
							<mat-option [value]="">— No change</mat-option>
							<mat-option *ngFor="let s of orderStatusesForSelect" [value]="+s.order_status_id">{{ s.name }} ({{ s.order_status_id }})</mat-option>
						</mat-select>
						<mat-hint>When a pending shipment is approved in the 3PL shipments UI.</mat-hint>
					</mat-form-field>
				</div>
			</div>
		</div>

		<!-- Products to push to 3PL -->
		<div *ngIf="integrationType === 'PGS'" formGroupName="config_json" class="config-section mb-3">
			<h5>Products to push to 3PL</h5>
			<p class="text-muted small mb-2">Which products to send when you use the Product master tab. Leave all empty to push all non-discontinued items.</p>
			<div class="row">
				<div class="col-md-4">
					<mat-form-field appearance="fill" class="w-100">
						<mat-label>Categories to push</mat-label>
						<mat-select formControlName="sku_categories" multiple placeholder="All categories">
							<mat-option *ngFor="let c of categories" [value]="c.categoryid">{{ c.categorydescription }}{{ c.categoryid ? ' (' + c.categoryid + ')' : '' }}</mat-option>
						</mat-select>
						<mat-hint>Leave empty to push all categories</mat-hint>
					</mat-form-field>
				</div>
				<div class="col-md-4">
					<mat-form-field appearance="fill" class="w-100">
						<mat-label>Product lines to push</mat-label>
						<mat-select formControlName="sku_lines" multiple placeholder="All product lines">
							<mat-option *ngFor="let p of productlines" [value]="p.line_field">{{ p.line_description }}{{ p.line_field ? ' (' + p.line_field + ')' : '' }}</mat-option>
						</mat-select>
						<mat-hint>Leave empty to push all product lines</mat-hint>
					</mat-form-field>
				</div>
				<div class="col-md-4">
					<mat-form-field appearance="fill" class="w-100">
						<mat-label>Brands to push</mat-label>
						<mat-select formControlName="sku_manufacturers" multiple placeholder="All brands" [compareWith]="compareByManufacturerId">
							<mat-option *ngFor="let b of brands" [value]="b.manufacturer_id">{{ b.name }}{{ b.mfg_code ? ' (' + b.mfg_code + ')' : '' }}</mat-option>
						</mat-select>
						<mat-hint>Leave empty to push all brands</mat-hint>
					</mat-form-field>
				</div>
			</div>
			<div *ngIf="(categories?.length === 0 || productlines?.length === 0 || brands?.length === 0) && !loading" class="text-muted small mt-1">
				<span *ngIf="categories?.length === 0">No categories found.</span>
				<span *ngIf="productlines?.length === 0">No product lines found.</span>
				<span *ngIf="brands?.length === 0">No brands found.</span>
			</div>
		</div>

		<div *ngIf="integrationType === 'PGS'" formGroupName="config_json" class="config-section mb-3">
			<h5>Inventory sync ledger</h5>
			<mat-slide-toggle formControlName="skip_gl_for_3pl_inventory">Skip GL posting for 3PL inventory pulls</mat-slide-toggle>
			<p class="text-muted small mb-0 mt-2">Stock movements are still written when quantities change. When off (default), GL follows manual inventory adjustment rules.</p>
		</div>

		<!-- PGS Status Mapping to ERP -->
		<div *ngIf="integrationType === 'PGS'" formGroupName="config_json" class="config-section mb-3">
			<h5>PGS Status Sync → ERP Order Status</h5>
			<p class="text-muted small mb-2">
				When the <code>tpl3pl_pgs_status_worker.php</code> cron polls PGS for order status updates (every 10 minutes), it can automatically update your ERP <code>salesorders.orderstatusid</code> based on the <strong>actual PGS status text</strong> returned by their API.
			</p>
			<div class="alert alert-info mb-3">
				<strong>📋 How to configure:</strong>
				<ol class="mb-0 mt-1 small">
					<li>Enter the PGS status keyword(s) from their API response (e.g., "awaiting fulfillment", "shipped")</li>
					<li>Select which ERP status to set when that keyword is found</li>
					<li>Use commas to match multiple keywords in one rule (e.g., "shipped, completed")</li>
					<li>Matching is case-insensitive substring (e.g., "awaiting" matches "Awaiting fulfillment")</li>
				</ol>
			</div>

			<div class="d-flex justify-content-between align-items-center mb-2">
				<h6 class="mb-0">PGS Status Mappings</h6>
				<button mat-raised-button color="accent" (click)="loadUniquePgsStatuses()" [disabled]="loadingPgsStatuses">
					<mat-icon>refresh</mat-icon> {{ loadingPgsStatuses ? 'Loading...' : 'Refresh from Database' }}
				</button>
			</div>
			
			<div *ngIf="loadingPgsStatuses" class="text-center p-3">
				<mat-spinner diameter="40"></mat-spinner>
				<p class="mt-2">Loading actual PGS statuses from your orders...</p>
			</div>

			<div *ngIf="!loadingPgsStatuses && uniquePgsStatuses.length === 0" class="alert alert-info">
				<strong>No PGS statuses found.</strong> Export some orders to 3PL first, then click "Refresh from Database" to see actual PGS status values and map them.
			</div>

			<div *ngIf="!loadingPgsStatuses && uniquePgsStatuses.length > 0">
				<p class="text-muted small mb-3">
					Found <strong>{{uniquePgsStatuses.length}}</strong> unique PGS status value(s) from your actual orders. 
					Select which ERP status each should map to.
				</p>
				<div *ngFor="let item of uniquePgsStatuses; let i = index" class="row mb-2 align-items-center">
					<div class="col-md-6">
						<div class="p-2 border rounded bg-light">
							<strong>PGS Status:</strong> "{{item.status}}"
							<span class="badge badge-secondary ml-2">{{item.count}} order(s)</span>
						</div>
					</div>
					<div class="col-md-1 text-center">
						<mat-icon>arrow_forward</mat-icon>
					</div>
					<div class="col-md-5">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Set ERP status to</mat-label>
							<mat-select [(ngModel)]="item.mappedStatusId" [ngModelOptions]="{standalone: true}" [compareWith]="compareByStatusId">
								<mat-option [value]="">— No change</mat-option>
								<mat-option *ngFor="let s of orderStatusesForSelect" [value]="+s.order_status_id">{{ s.name }} ({{ s.order_status_id }})</mat-option>
							</mat-select>
						</mat-form-field>
					</div>
				</div>
			</div>

			<div class="row mt-3">
				<div class="col-md-6 mb-3">
					<h6 class="mb-2">Status Rank Order (Recommended)</h6>
					<p class="text-muted small mb-2">
						<strong>Prevents conflicts</strong> between PGS sync and shipment events. Status can only move forward in this order.
					</p>
					<mat-form-field appearance="fill" class="w-100">
						<mat-label>Rank Order (JSON Array)</mat-label>
						<textarea 
							matInput 
							formControlName="pgs_sync_erp_status_rank_json" 
							rows="3" 
							placeholder='[1, 5, 23, 29, 50]'></textarea>
						<mat-hint>Example: 1 (New) → 5 (Dispatched/Picking) → 23 (Packed) → 29 (In Transit) → 50 (Invoiced). Leave empty for default.</mat-hint>
					</mat-form-field>
				</div>

				<div class="col-md-6 mb-3">
					<h6 class="mb-2">Protected ERP Statuses</h6>
					<p class="text-muted small mb-2">
						Orders in these statuses won't be updated by PGS sync (except cancellation).
					</p>
					<mat-form-field appearance="fill" class="w-100">
						<mat-label>Skip when current status is</mat-label>
						<mat-select formControlName="pgs_sync_erp_skip_when_orderstatusid" multiple placeholder="Default: [80] (Credit Hold)">
							<mat-option *ngFor="let s of orderStatusesForSelect" [value]="+s.order_status_id">{{ s.name }} ({{ s.order_status_id }})</mat-option>
						</mat-select>
						<mat-hint>Default: 80 (Credit Hold). Cancellation (90) always bypasses this.</mat-hint>
					</mat-form-field>
				</div>
			</div>
		</div>

		<!-- API Configuration -->
		<div *ngIf="integrationType === 'API'" formGroupName="config_json">
			<div formGroupName="api" class="config-section">
				<h5>API Configuration</h5>
				<div class="row">
					<div class="col-md-12">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Base URL</mat-label>
							<input matInput formControlName="baseUrl" placeholder="https://api.3plprovider.com">
						</mat-form-field>
					</div>
				</div>
				<div class="row">
					<div class="col-md-6">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>API Key</mat-label>
							<input matInput type="password" formControlName="apiKey">
						</mat-form-field>
					</div>
					<div class="col-md-6">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>API Secret</mat-label>
							<input matInput type="password" formControlName="apiSecret">
						</mat-form-field>
					</div>
				</div>
				<div class="row">
					<div class="col-md-6">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Timeout (seconds)</mat-label>
							<input matInput type="number" formControlName="timeout">
						</mat-form-field>
					</div>
					<div class="col-md-6">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Retry Attempts</mat-label>
							<input matInput type="number" formControlName="retryAttempts">
						</mat-form-field>
					</div>
				</div>
			</div>
		</div>

		<!-- PGS Configuration -->
		<div *ngIf="integrationType === 'PGS'" formGroupName="config_json">
			<div formGroupName="pgs" class="config-section">
				<h5>PGS Configuration</h5>
				<div class="row">
					<div class="col-md-12">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Base URL</mat-label>
							<input matInput formControlName="baseUrl" placeholder="https://api.pgs360.com/CustomerIntegrator">
						</mat-form-field>
					</div>
				</div>
				<div class="row">
					<div class="col-md-12">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Token URL (optional)</mat-label>
							<input matInput formControlName="tokenUrl" placeholder="https://api.pgs360.com/fulfillment_client/security/token">
							<mat-hint>Leave blank to use Base URL + /api/Security/Token. Production fulfillment often uses the placeholder URL above.</mat-hint>
						</mat-form-field>
					</div>
				</div>
				<div class="row">
					<div class="col-md-6">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>User ID</mat-label>
							<input matInput formControlName="userId">
						</mat-form-field>
					</div>
					<div class="col-md-6">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Password</mat-label>
							<input matInput type="password" formControlName="passwd">
						</mat-form-field>
					</div>
				</div>
				<div class="row">
					<div class="col-md-4">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Default Warehouse</mat-label>
							<mat-select formControlName="defaultWarehouse">
								<mat-option value="CM">CM</mat-option>
								<mat-option value="PICO">PICO</mat-option>
								<mat-option value="FW">FW</mat-option>
								<mat-option value="NJ">NJ</mat-option>
							</mat-select>
						</mat-form-field>
					</div>
					<div class="col-md-4">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Default Outbound Channel</mat-label>
							<mat-select formControlName="defaultOutboundChannel">
								<mat-option value="B2B">B2B</mat-option>
								<mat-option value="B2C">B2C</mat-option>
							</mat-select>
						</mat-form-field>
					</div>
					<div class="col-md-4">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>B2C Service Type</mat-label>
							<input matInput formControlName="b2cServiceType" placeholder="Ground">
						</mat-form-field>
					</div>
				</div>
				<div class="row">
					<div class="col-md-4">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Inventory page size (maxResults)</mat-label>
							<input matInput type="number" formControlName="inventoryMaxResults" min="1" max="2000">
							<mat-hint>PGS returns ~30 lines if maxResults is omitted. Sync paginates with this page size until the catalog is complete (default 500).</mat-hint>
						</mat-form-field>
					</div>
					<div class="col-md-4">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>ERP location for 3PL inventory (optional)</mat-label>
							<input matInput formControlName="inventoryLoccode" placeholder="e.g. 01">
							<mat-hint>Must match a <code>locations.loccode</code>. Used as the default ERP warehouse when PGS sends a code that is not already an ERP location.</mat-hint>
						</mat-form-field>
					</div>
				</div>
				<div class="row">
					<div class="col-md-12">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>PGS warehouse → ERP location map (JSON)</mat-label>
							<textarea matInput formControlName="warehouseMapJson" rows="4" placeholder='{ "01": "CM" }'></textarea>
							<mat-hint>Maps PGS inventory <code>warehouse</code> values to ERP <code>loccode</code>. Example: one key is your ERP location, value is the PGS code (e.g. CM). Either direction works. Leave empty only if every PGS code already exists as an ERP location.</mat-hint>
						</mat-form-field>
					</div>
				</div>
				<div formGroupName="lob" class="row mt-2">
					<div class="col-md-12 mb-2">
						<h6 class="mb-0">SKU line of business</h6>
					</div>
					<div class="col-md-4">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Default</mat-label>
							<input matInput formControlName="default" placeholder="Kevin Murphy">
							<mat-hint>If nothing else matches.</mat-hint>
						</mat-form-field>
					</div>
					<div class="col-md-8">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Manufacturer ID → LOB (JSON)</mat-label>
							<textarea matInput formControlName="byManufacturerIdJson" rows="4" placeholder='{ "26": "Kevin Murphy", "221": "Eleven" }'></textarea>
							<mat-hint>Keys: <code>manufacturer_id</code> from your manufacturer table.</mat-hint>
						</mat-form-field>
					</div>
				</div>
			</div>
		</div>

		<!-- EDI Configuration -->
		<div *ngIf="integrationType === 'EDI'" formGroupName="config_json">
			<div formGroupName="edi" class="config-section">
				<h5>EDI Configuration</h5>
				<div class="row">
					<div class="col-md-6">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Trading Partner ID</mat-label>
							<input matInput formControlName="tradingPartnerId">
						</mat-form-field>
					</div>
					<div class="col-md-3">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>ISA Qualifier</mat-label>
							<input matInput formControlName="isaQualifier">
						</mat-form-field>
					</div>
					<div class="col-md-3">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>GS Qualifier</mat-label>
							<input matInput formControlName="gsQualifier">
						</mat-form-field>
					</div>
				</div>
			</div>
		</div>

		<!-- SFTP/FTP Configuration -->
		<div *ngIf="integrationType === 'SFTP' || integrationType === 'FTP'" formGroupName="config_json">
			<div formGroupName="sftp" class="config-section">
				<h5>{{ integrationType }} Configuration</h5>
				<div class="row">
					<div class="col-md-6">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Host</mat-label>
							<input matInput formControlName="host" placeholder="sftp.3plprovider.com">
						</mat-form-field>
					</div>
					<div class="col-md-6">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Port</mat-label>
							<input matInput type="number" formControlName="port">
						</mat-form-field>
					</div>
				</div>
				<div class="row">
					<div class="col-md-6">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Username</mat-label>
							<input matInput formControlName="username">
						</mat-form-field>
					</div>
					<div class="col-md-6">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Password</mat-label>
							<input matInput type="password" formControlName="password">
						</mat-form-field>
					</div>
				</div>
				<div class="row">
					<div class="col-md-6">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Remote Path (Inbound)</mat-label>
							<input matInput formControlName="remotePath">
						</mat-form-field>
					</div>
					<div class="col-md-6">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Outbound Path</mat-label>
							<input matInput formControlName="outboundPath">
						</mat-form-field>
					</div>
				</div>
			</div>
		</div>

		<div class="row mt-4">
			<div class="col-md-12">
				<button mat-raised-button color="primary" (click)="saveConfig()" [disabled]="saving || configForm.invalid">
					<mat-spinner *ngIf="saving" diameter="18" class="d-inline-block mr-1"></mat-spinner>
					<span *ngIf="!saving">Save Configuration</span>
					<span *ngIf="saving">Saving...</span>
				</button>
				<button mat-raised-button color="accent" (click)="testConnection()" [disabled]="testing" class="ml-2">
					<mat-spinner *ngIf="testing" diameter="18" class="d-inline-block mr-1"></mat-spinner>
					<span *ngIf="!testing">Test Connection</span>
					<span *ngIf="testing">Testing...</span>
				</button>
			</div>
		</div>

		<div *ngIf="testResult" class="mt-3">
			<div [ngClass]="{'alert-success': testResult.success, 'alert-danger': !testResult.success}" class="alert">
				<strong>{{ testResult.success ? 'Success' : 'Failed' }}:</strong> {{ testResult.message }}
			</div>
			<div *ngIf="!testResult.success && testResult.response" class="mt-2">
				<details>
					<summary>API response</summary>
					<pre class="mb-0 p-2 bg-light small config-test-response-pre">{{ testResult.response | json }}</pre>
				</details>
			</div>
		</div>
	</form>

	<div *ngIf="loading" class="text-center p-4">
		<mat-spinner diameter="50"></mat-spinner>
		<p>Loading configuration...</p>
	</div>
</div>
